import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, RefreshCw } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import { useToast, ToastContainer } from '../common/Toast';
import api from '../../services/api';

const STATUS_STAGE_MAP = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  EVALUATION: 'Evaluated',
  AWARDED: 'Awarded',
  CANCELLED: 'Contract',
};

const deriveStage = (rfp) => {
  const lifecycle = rfp?.metadata?.lifecycleStage;
  if (lifecycle) {
    return lifecycle;
  }
  const status = String(rfp?.status || '').toUpperCase();
  return STATUS_STAGE_MAP[status] || 'Draft';
};

const deriveApprovalStatus = (rfp, stage) => {
  const explicit = rfp?.metadata?.approvalStatus;
  if (explicit) {
    return explicit;
  }

  if (stage === 'Approved') return 'Approved';
  if (stage === 'Review' || stage === 'Generated') return 'Pending';
  return 'Not Submitted';
};

const getSubmitter = (rfp) => {
  if (rfp?.createdBy?.name) return rfp.createdBy.name;
  if (rfp?.createdBy?.email) return rfp.createdBy.email;
  if (typeof rfp?.createdBy === 'string') return rfp.createdBy.slice(-8);
  return '-';
};

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const normalizeQueueRow = (item) => ({
  _id: item?._id || item?.draftId,
  draftId: item?.draftId || item?._id,
  rfpNumber: item?.rfpNumber || `AI-${String(item?._id || '').slice(-6).toUpperCase()}`,
  submittedBy: item?.submittedBy || '-',
  stage: item?.stage || 'Review',
  approvalStatus: item?.approvalStatus || 'Pending',
  comments: item?.comments || '-',
  source: item?.source || 'ai-draft',
});

const statusPill = (status) => {
  const map = {
    Pending: { bg: '#fef3c7', color: '#92400e' },
    Approved: { bg: '#dcfce7', color: '#166534' },
    Rejected: { bg: '#fee2e2', color: '#991b1b' },
    'Not Submitted': { bg: '#f3f4f6', color: '#4b5563' },
  };
  const cfg = map[status] || map['Not Submitted'];

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.color,
      }}
    >
      {status}
    </span>
  );
};

const RfpApprovalQueue = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/rfp/approvals');
      const rows = extractRows(response.data).map(normalizeQueueRow);
      setQueue(rows);
    } catch (err) {
      try {
        const fallback = await api.get('/sourcing/rfps');
        const allRfps = extractRows(fallback);
        const derivedRows = allRfps
          .map((rfp) => {
            const stage = deriveStage(rfp);
            return {
              _id: rfp._id,
              rfpNumber: rfp.rfpNumber,
              submittedBy: getSubmitter(rfp),
              stage,
              approvalStatus: deriveApprovalStatus(rfp, stage),
              comments: rfp?.metadata?.approvalComments || '-',
            };
          })
          .filter((row) => row.approvalStatus !== 'Not Submitted');
        setQueue(derivedRows);
      } catch (fallbackErr) {
        setQueue([]);
        showError(
          fallbackErr?.response?.data?.message ||
            fallbackErr.message ||
            err?.response?.data?.message ||
            err.message ||
            'Failed to load approval queue',
        );
      }
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const isPending = (row) => row.approvalStatus === 'Pending';

  const handleApprove = useCallback(
    async (row) => {
      try {
        await api.post(`/rfp/${row._id}/approve`, {
          decision: 'APPROVED',
        });
        success('RFP approved successfully.');
        await loadQueue();
      } catch (err) {
        showError(
          err?.response?.data?.message ||
            err.message ||
            'Failed to approve RFP. No changes were saved.',
        );
      }
    },
    [loadQueue, showError, success],
  );

  const handleReject = useCallback(
    async (row) => {
      try {
        await api.post(`/rfp/${row._id}/approve`, {
          decision: 'REJECTED',
          comments: 'Rejected and sent back to builder',
        });
        success('RFP rejected. Sent back to builder.');
        await loadQueue();
      } catch (err) {
        showError(
          err?.response?.data?.message ||
            err.message ||
            'Failed to reject RFP. No changes were saved.',
        );
      }
    },
    [loadQueue, showError, success],
  );

  const columns = useMemo(
    () => [
      {
        key: 'rfpNumber',
        label: 'RFP ID',
        render: (_, row) => <strong>{row.rfpNumber || '-'}</strong>,
      },
      {
        key: 'submittedBy',
        label: 'Submitted By',
      },
      {
        key: 'stage',
        label: 'Stage',
      },
      {
        key: 'approvalStatus',
        label: 'Status',
        render: (_, row) => statusPill(row.approvalStatus),
      },
      {
        key: 'comments',
        label: 'Comments',
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_, row) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-success"
              disabled={!isPending(row)}
              onClick={(e) => {
                e.stopPropagation();
                handleApprove(row);
              }}
            >
              <CheckCircle2 size={14} className="me-1" />
              Approve
            </button>
            <button
              className="btn btn-sm btn-danger"
              disabled={!isPending(row)}
              onClick={(e) => {
                e.stopPropagation();
                handleReject(row);
              }}
            >
              <XCircle size={14} className="me-1" />
              Reject
            </button>
          </div>
        ),
      },
    ],
    [handleApprove, handleReject],
  );

  const headerActions = [
    {
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      variant: 'outline',
      onClick: loadQueue,
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="APPROVAL SCREEN"
        description="Approval queue for AI-generated RFP drafts awaiting review."
        actions={headerActions}
      />

      <div className="page-container">
        <DataTable
          data={queue}
          columns={columns}
          loading={loading}
          showActions={false}
          searchable
          showFilterButton={false}
          onRowClick={(row) =>
            navigate('/sourcing/rfps/create/workspace', {
              state: {
                draftId: row.draftId || row._id,
                sourceRfpId: row._id,
                source: 'approval-queue',
              },
            })
          }
          emptyMessage="No RFP approvals available."
        />
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default RfpApprovalQueue;
